import { LecturerContract, LecturerContractFile } from '../entities/LecturerContract.js';
import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getLecturerContractsByIDs = registerQuery({
  operationName: 'getLecturerContractsByIDs',
  type: () => [LecturerContract],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<LecturerContract>>(gqlFields: SelSet) =>
      readEntitiesByIDs(LecturerContract, ids, gqlFields),
});

export const subscribeLecturerContractsByIDs = registerSubscription({
  operationName: 'subscribeLecturerContractsByIDs',
  type: () => [LecturerContract],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<LecturerContract>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(LecturerContract, ids, gqlFields),
});

export const getAllLecturerContracts = registerQuery({
  operationName: 'getAllLecturerContracts',
  type: () => [LecturerContract],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<LecturerContract>>(gqlFields: SelSet) =>
      readAllEntities(LecturerContract, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllLecturerContracts = registerSubscription({
  operationName: 'subscribeAllLecturerContracts',
  type: () => [LecturerContract],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<LecturerContract>>(gqlFields: SelSet) =>
      watchAllEntities(LecturerContract, gqlFields, { skip, limit, excludeIDs }),
});

export const getLecturerContractByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<LecturerContract>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getLecturerContractsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeLecturerContractByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<LecturerContract>>(gqlFields: SelSet) =>
    id ? subscribeLecturerContractsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);

export const getLecturerContractFilesByIDs = registerQuery({
  operationName: 'getLecturerContractFilesByIDs',
  type: () => [LecturerContractFile],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<LecturerContractFile>>(gqlFields: SelSet) =>
      readEntitiesByIDs(LecturerContractFile, ids, gqlFields),
});

export const subscribeLecturerContractFilesByIDs = registerSubscription({
  operationName: 'subscribeLecturerContractFilesByIDs',
  type: () => [LecturerContractFile],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<LecturerContractFile>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(LecturerContractFile, ids, gqlFields),
});

export const getAllLecturerContractFiles = registerQuery({
  operationName: 'getAllLecturerContractFiles',
  type: () => [LecturerContractFile],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<LecturerContractFile>>(gqlFields: SelSet) =>
      readAllEntities(LecturerContractFile, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllLecturerContractFiles = registerSubscription({
  operationName: 'subscribeAllLecturerContractFiles',
  type: () => [LecturerContractFile],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<LecturerContractFile>>(gqlFields: SelSet) =>
      watchAllEntities(LecturerContractFile, gqlFields, { skip, limit, excludeIDs }),
});

export const getLecturerContractFileByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<LecturerContractFile>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getLecturerContractFilesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeLecturerContractFileByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<LecturerContractFile>>(gqlFields: SelSet) =>
    id ? subscribeLecturerContractFilesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
